import { type MouseEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import { Badge, Button, Icon, Tooltip } from '@loadsmart/miranda-react'

import { ChatRuntimeProvider } from 'core/chat/ui/ChatContext'
import { ChatWindow } from 'core/chat/ui/ChatWindow'
import { useChatDrawer } from 'core/chat/ui/hooks/useChatDrawer'
import { useListChat } from 'core/chat/ui/hooks/useListChat'
import { Config, useConfig } from 'core/remote-config'

import { type AgnosticShipment } from '../domain/AgnosticShipment'
import {
  getShipmentListChatState,
  getShipmentListChatTooltip,
  type ShipmentListChatState,
} from '../domain/shipmentListChat'
import {
  mapAgnosticShipmentToChatSource,
  mapShipmentToListChatCreate,
} from '../presenters/shipmentListChatPresenter'
import { getShipment } from '../use-cases/getShipment'

export type ShipmentListChatProps = {
  shipment: AgnosticShipment
}

function ShipmentListChatDrawer({
  shipment,
  isOpen,
  onClose,
  onClosed,
  onReady,
}: {
  shipment: AgnosticShipment
  isOpen: boolean
  onClose: () => void
  onClosed: () => void
  onReady: () => void
}) {
  const source = useMemo(() => mapAgnosticShipmentToChatSource(shipment), [shipment])
  const resolveShipper = useCallback(async () => {
    const detail = await getShipment(shipment.uuid, { accessToken: source.inviteToken })
    return mapShipmentToListChatCreate(detail)
  }, [shipment.uuid, source.inviteToken])
  const chat = useListChat({ source, resolveShipper })
  const { conversation, isLoading, me, openConversation } = chat
  const isReady = !isLoading && Boolean(me) && Boolean(conversation)

  useEffect(() => {
    if (isLoading) return

    if (!me) {
      toast.error('Unable to open chat. Please try again.')
      onClose()
      onClosed()
      return
    }

    if (conversation) {
      onReady()
      return
    }

    openConversation().catch(() => {
      toast.error('Unable to open chat. Please try again.')
      onClose()
      onClosed()
    })
  }, [conversation, isLoading, me, onClose, onClosed, onReady, openConversation])

  if (!isReady) return null

  return (
    <div onClick={event => event.stopPropagation()}>
      <ChatRuntimeProvider chat={chat}>
        <ChatWindow isOpen={isOpen} onClose={onClose} onClosed={onClosed} />
      </ChatRuntimeProvider>
    </div>
  )
}

function ShipmentListChatIcon({
  state,
  tooltip,
  onClick,
  isLoading,
}: {
  state: Exclude<ShipmentListChatState, 'hidden'>
  tooltip: string
  onClick: (event: MouseEvent) => void
  isLoading: boolean
}) {
  const iconColor = state === 'no-chat' ? 'color-text-disabled' : undefined

  return (
    <Tooltip message={tooltip} placement="top" trigger="hover">
      <Button
        type="button"
        variant={state === 'unread' ? 'icon' : 'icon-secondary'}
        aria-label={tooltip}
        onClick={onClick}
        loading={isLoading}
        data-testid="shipment-list-chat"
      >
        {state === 'unread' ? (
          <Badge placement="top-right" size="small" color="color-semantic-error">
            <Icon name="chat" size="20px" aria-hidden />
          </Badge>
        ) : (
          <Icon name="chat" size="20px" color={iconColor} aria-hidden />
        )}
      </Button>
    </Tooltip>
  )
}

export function ShipmentListChat({ shipment }: ShipmentListChatProps) {
  const isEnabled = useConfig(Config.enableCarrierShipmentListChat)
  const { isOpen, isMounted, open, close, onClosed } = useChatDrawer()
  const [isPreparing, setIsPreparing] = useState(false)
  const [readChat, setReadChat] = useState<AgnosticShipment['chat']>()
  const listState = getShipmentListChatState(shipment)
  const state = listState !== 'hidden' && readChat === shipment.chat ? 'read' : listState
  const handleReady = useCallback(() => {
    setIsPreparing(false)
    setReadChat(shipment.chat)
  }, [shipment.chat])
  const handleClose = useCallback(() => {
    setIsPreparing(false)
    close()
  }, [close])
  const handleClosed = useCallback(() => {
    setIsPreparing(false)
    onClosed()
  }, [onClosed])

  if (!isEnabled || state === 'hidden') return null

  const tooltip = getShipmentListChatTooltip(state, shipment.chat.unreadByCarrierCount)

  function handleClick(event: MouseEvent) {
    event.stopPropagation()
    setIsPreparing(true)
    open()
  }

  return (
    <>
      <ShipmentListChatIcon
        state={state}
        tooltip={tooltip}
        onClick={handleClick}
        isLoading={isPreparing}
      />
      {isMounted && (
        <ShipmentListChatDrawer
          shipment={shipment}
          isOpen={isOpen}
          onClose={handleClose}
          onClosed={handleClosed}
          onReady={handleReady}
        />
      )}
    </>
  )
}
